import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 767px)').matches
            : false
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767px)');

        const handleChange = (event) => {
            setIsMobile(event.matches);
        };

        setIsMobile(mediaQuery.matches);

        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    return isMobile;
}

const dailyMissionTiers = [
    {
        tier: '1',
        turnover: '1,000 - 14,999',
        deposit: '200',
        entries: '1 Ticket',
    },
    {
        tier: '2',
        turnover: '15,000 - 49,999',
        deposit: '3,000',
        entries: '5 Tickets',
    },
    {
        tier: '3',
        turnover: '50,000 - 149,999',
        deposit: '10,000',
        entries: '15 Tickets',
    },
    {
        tier: '4',
        turnover: '150,000 - 499,999',
        deposit: '30,000',
        entries: '50 Tickets',
    },
    {
        tier: '5',
        turnover: '500,000 above',
        deposit: '100,000',
        entries: '100 Tickets',
    },
];

const leaderboardRewards = [
    {
        rank: 'R1',
        reward: '200',
    },
    {
        rank: 'R2 - R3',
        reward: '100',
    },
    {
        rank: 'R4 - R10',
        reward: '50',
    },
    {
        rank: 'R11 - R30',
        reward: '20',
    },
];

export const spinFuelMilestones = [
    {
        milestone: 'M1',
        turnover: '15,000 - 74,999',
        reward: '50',
    },
    {
        milestone: 'M2',
        turnover: '75,000 - 349,999',
        reward: '80',
    },
    {
        milestone: 'M3',
        turnover: '350,000 - 1,499,999',
        reward: '150',
    },
    {
        milestone: 'M4',
        turnover: '1.5M',
        reward: '1,000',
    },
];

export default function FortuneRushTermsAndConditions({ onClose, onBack }) {
    const isMobile = useIsMobile();

    const Shell = isMobile ? 'div' : motion.div;

    const desktopMotionProps = !isMobile
        ? {
              initial: {
                  opacity: 0,
                  scale: 0.85,
                  y: 30,
              },
              animate: {
                  opacity: 1,
                  scale: 1,
                  y: 0,
              },
              exit: {
                  opacity: 0,
                  scale: 0.9,
                  y: 20,
              },
              transition: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 25,
              },
          }
        : {};
    return (
        <Shell className="fr-tnc-shell" {...desktopMotionProps}>
            <div className="fr-tnc-glow" aria-hidden="true" />

            <section
                className="fr-tnc-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="fortune-rush-tnc-title"
            >
                <div className="fr-tnc-candy-top" aria-hidden="true" />

                {/* HEADER */}

                <header className="fr-tnc-header">
                    <img
                        src="/images/game/jili-rush/JILI-RUSH-T&C.webp"
                        alt=""
                    />

                    <p className="fr-tnc-subtitle">
                        Every Spin Counts, Every Milestone Pays!
                    </p>

                    {onClose && (
                        <button
                            type="button"
                            className="fr-tnc-close"
                            onClick={onClose}
                            aria-label="Close terms and conditions"
                        >
                            ×
                        </button>
                    )}
                </header>

                <div className="fr-tnc-divider" aria-hidden="true">
                    <span />
                    <i>✦</i>
                    <span />
                </div>

                <div className="fr-tnc-scroll">
                    <section className="fr-tnc-mechanic-section">
                        <h2>Promotion Mechanics</h2>
                        <p>
                            <strong>Promo Period:</strong> October 08, 2026
                            (12NN) - November 05, 2026 (11:59AM)
                        </p>
                        <span className="fr-tnc-paragraph">
                            Unlock rewards in three different ways while playing
                        </span>
                        <br />
                        <p>
                            <strong>1. Daily Missions:</strong> Complete daily
                            targets to earn tickets for the Month-End Raffle
                            Draw. To qualify for a daily tier, players must meet
                            both the <strong>Daily Turnover Target</strong> AND
                            the <strong>Cumulative Target Deposit</strong>{' '}
                            requirement within the same cut-off day{' '}
                            <strong>(12:00 PM – 11:30 AM)</strong>.
                        </p>
                        <div className="fr-tnc-table-wrapper">
                            <table className="fr-tnc-table">
                                <thead>
                                    <tr>
                                        <th>Tier</th>
                                        <th>
                                            Daily Turnover
                                            <br />
                                            Target
                                        </th>
                                        <th>
                                            Target
                                            <br />
                                            Deposit
                                        </th>
                                        <th>
                                            Raffle
                                            <br />
                                            Entries
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {dailyMissionTiers.map((item) => (
                                        <tr key={item.tier}>
                                            <td>
                                                <strong>{item.tier}</strong>
                                            </td>
                                            <td>{item.turnover}</td>
                                            <td>{item.deposit}</td>
                                            <td>{item.entries}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="fr-tnc-highlight-note">
                            <span>⚠️</span>

                            <p>
                                <strong>Note:</strong> Players receive the
                                ticket allocation for the{' '}
                                <strong>
                                    highest single tier unlocked per day
                                </strong>
                                . All tickets accumulated throughout the 28 days
                                carry over to the Month-End Draw.
                            </p>
                        </div>
                        <br />
                        <p>
                            <strong>Month-End Raffle Draw:</strong> At the end
                            of the 28-day campaign, all accumulated raffle
                            tickets will be entered into a final prize draw.
                        </p>
                        <div className="fr-tnc-table-wrapper">
                            <table className="fr-tnc-table">
                                <thead>
                                    <tr>
                                        <th>Raffle Prize</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr>
                                        <td>
                                            Spa &amp; Dining Experience Voucher
                                        </td>
                                        <td>25,000.00</td>
                                    </tr>
                                    <tr>
                                        <td>Grocery Voucher </td>
                                        <td>10,000.00</td>
                                    </tr>
                                    <tr>
                                        <td>Cash Prize </td>
                                        <td>3,000.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="fr-tnc-highlight-note">
                            <span>📌</span>

                            <p>
                                <strong>Winner Announcement:</strong> Winners
                                will be announced via the platform&apos;s
                                official announcement page and notified through
                                internal site inbox messages.
                            </p>
                        </div>
                        <br />
                        <p>
                            <strong>
                                2. Weeks 1 &amp; 3: Free Spins Leaderboard:{' '}
                            </strong>
                            Compete for a top 30 spot on the weekly turnover
                            leaderboard by playing JILI Slots from{' '}
                            <strong>Thursday 12NN to Thursday 11:30AM</strong>!
                        </p>
                        <div className="fr-tnc-detail-list">
                            <p>
                                <strong>Minimum Qualifying Turnover:</strong>{' '}
                                5,000 minimum turnover to qualify in the
                                leaderboard
                                <br />
                                <strong>Crediting Day:</strong> Every Thursday
                                at 3 PM
                                <br />
                                <strong>Tie-Breaker:</strong> If two players end
                                with the exact same turnover, the player who
                                reached the turnover amount first takes the
                                higher rank.
                            </p>
                        </div>
                        <div className="fr-tnc-table-title">
                            Free Spins Leaderboard
                        </div>
                        <div className="fr-tnc-table-wrapper">
                            <table className="fr-tnc-table">
                                <thead>
                                    <tr>
                                        <th>Rank Position</th>

                                        <th>
                                            Reward with 5
                                            <br />
                                            PHP bet value
                                        </th>

                                        <th>Rollover Requirement</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {leaderboardRewards.map((item, index) => (
                                        <tr key={item.rank}>
                                            <td>
                                                <strong>{item.rank}</strong>
                                            </td>

                                            <td>{item.reward}</td>

                                            {index === 0 && (
                                                <td
                                                    rowSpan={
                                                        leaderboardRewards.length
                                                    }
                                                >
                                                    Winnings has{' '}
                                                    <strong>NO Rollover</strong>{' '}
                                                    requirement
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="fr-tnc-highlight-note">
                            <span>⚠️</span>

                            <p>
                                <strong>Free Spin Expiration:</strong> Free
                                spins credited on Thursdays must be used within
                                seven (7) days of issuance. Unused free spins
                                will automatically expire.
                            </p>
                        </div>
                        <br />
                        <p>
                            <strong>3. Weeks 2 &amp; 4: Spin Fuel: </strong>
                            Reach major daily turnover thresholds to collect
                            your spin fuel after the daily cutoff.
                        </p>
                        <div className="fr-tnc-table-title">Spin Fuel</div>
                        <div className="fr-tnc-table-wrapper">
                            <table className="fr-tnc-table">
                                <thead>
                                    <tr>
                                        <th>Milestone</th>
                                        <th>Turnover Target</th>
                                        <th>
                                            Free Credits
                                            <br />
                                            Reward
                                        </th>
                                        <th>
                                            Rollover
                                            <br />
                                            Requirement
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {spinFuelMilestones.map((item, index) => (
                                        <tr key={item.milestone}>
                                            <td>
                                                <strong>
                                                    {item.milestone}
                                                </strong>
                                            </td>

                                            <td>{item.turnover}</td>

                                            <td>{item.reward}</td>

                                            {index === 0 && (
                                                <td
                                                    rowSpan={
                                                        spinFuelMilestones.length
                                                    }
                                                >
                                                    <strong>1x Rollover</strong>{' '}
                                                    requirement
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="fr-tnc-highlight-note">
                            <span>⚠️</span>

                            <p>
                                <strong>Prize Expiration:</strong> Triggered
                                Spin Fuel Rewards do not expire daily! Players
                                can claim their earned rewards via the floating
                                icon at any time during the promo period.
                            </p>
                        </div>
                        <div className="fr-tnc-highlight-note">
                            <span>💡</span>

                            <p>
                                <strong>How Milestone Rewards Work: </strong>As
                                your daily turnover increases, each milestone
                                you reach unlocks a claimable Free Credits
                                reward.
                            </p>
                        </div>
                    </section>

                    <section className="fr-tnc-mechanic-section">
                        <h2>Terms & Conditions </h2>

                        <br />
                        <p>
                            <strong>1. Eligibility: </strong> Open to all
                            registered FGH players.
                        </p>
                        <p>
                            <strong>
                                2. Qualification & Game Restrictions:{' '}
                            </strong>{' '}
                            Only valid wagers placed on JILI Slot games count
                            toward turnover accumulation. Turnover on
                            non-qualifying games (e.g., JILI Fishing, Table
                            Games, or other game providers) will NOT count
                            toward daily milestones or leaderboard ranks..
                        </p>
                        <p>
                            <strong>3. Reset Cut-off Time: </strong>Daily
                            milestone trackers and daily mission targets reset
                            every day at 11:30 AM.
                        </p>
                        <p>
                            <strong>4. Rollover Requirement: </strong>Free
                            Credits are subject to a strict 1x rollover
                            requirement before a withdrawal can be processed.
                            Winnings derived from the awarded free spins has no
                            rollover requirement and can be withdrawn
                            immediately.
                        </p>
                        <p>
                            <strong>5. One Account Policy: </strong>One Account
                            Policy
                        </p>
                        <p>
                            <strong>6. Management Rights: </strong> FGH reserves
                            the right to alter, amend, or terminate this
                            promotion, or any aspect of it, at its sole
                            discretion at any time. In all matters relating to
                            this promotion, including eligibility, winner
                            selection, and dispute resolutions, the decisions of
                            the FGH management team are absolute, final, and not
                            subject to appeal. Any form of abuse or unfair play
                            detected will result in the immediate forfeiture of
                            rewards and potential account suspension.
                        </p>
                    </section>

                    {/* FINAL NOTE */}
                    <div className="fr-tnc-note">
                        <span className="fr-tnc-note-icon">!</span>

                        <p>
                            By participating in JILI Fortune Rush, the player
                            confirms that they have read, understood, and agreed
                            to these Terms &amp; Conditions together with the
                            published promotion mechanics.
                        </p>
                    </div>
                </div>

                <footer className="fr-tnc-footer">
                    <button
                        type="button"
                        className="fr-tnc-button"
                        onClick={onBack || onClose}
                    >
                        <span>
                            <FontAwesomeIcon icon={faXmark} />
                        </span>
                    </button>
                </footer>
            </section>
        </Shell>
    );
}
